import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { PlatformClient } from "../../platform/supabase";

type Notification={
  notificationId:string;
  alertKey:string;
  severity:string;
  category:string;
  title:string;
  body?:string|null;
  actionPath?:string|null;
  status:"UNREAD"|"READ"|"DISMISSED";
  sourceActive:boolean;
  firstSeenAt:string;
  lastSeenAt:string;
  readAt?:string|null;
};
type Inbox={counts:{unread:number;read:number};items:Notification[]};

function go(path?:string|null){
  if(!path)return;
  window.history.pushState({},"",path);
  window.dispatchEvent(new PopStateEvent("popstate"));
}

function fmt(value:string){
  return new Date(value).toLocaleString("ko-KR",{month:"2-digit",day:"2-digit",hour:"2-digit",minute:"2-digit"});
}

export function CenterNotificationBell({client,schoolId}:{client:PlatformClient;schoolId:string}){
  const [inbox,setInbox]=useState<Inbox|null>(null);
  const [open,setOpen]=useState(false);
  const [busy,setBusy]=useState("");
  const [error,setError]=useState("");
  const [refresh,setRefresh]=useState(0);
  const triggerRef=useRef<HTMLButtonElement|null>(null);
  const [anchor,setAnchor]=useState({top:0,right:10});

  useEffect(()=>{
    let active=true;
    setError("");
    (async()=>{
      const sync=await client.rpc("center_sync_notification_inbox",{p_school_id:schoolId});
      if(!active)return;
      if(sync.error){setError(sync.error.message);return;}
      const result=await client.rpc("center_notification_inbox",{p_school_id:schoolId});
      if(!active)return;
      if(result.error){setError(result.error.message);return;}
      setInbox(result.data as Inbox);
    })();
    return()=>{active=false;};
  },[client,schoolId,refresh]);

  useEffect(()=>{
    if(!open)return;
    const position=()=>{
      const trigger=triggerRef.current;
      if(!trigger)return;
      const rect=trigger.getBoundingClientRect();
      setAnchor({
        top:Math.max(10,rect.bottom+7),
        right:Math.max(10,window.innerWidth-rect.right)
      });
    };
    const closeOnEscape=(event:KeyboardEvent)=>{
      if(event.key==="Escape")setOpen(false);
    };
    position();
    window.addEventListener("resize",position);
    window.addEventListener("scroll",position,true);
    window.addEventListener("keydown",closeOnEscape);
    return()=>{
      window.removeEventListener("resize",position);
      window.removeEventListener("scroll",position,true);
      window.removeEventListener("keydown",closeOnEscape);
    };
  },[open]);

  async function state(item:Notification,action:"READ"|"UNREAD"|"DISMISS"){
    setBusy(item.notificationId);
    const {error}=await client.rpc("center_set_notification_state",{
      p_school_id:schoolId,p_notification_id:item.notificationId,p_action:action
    });
    setBusy("");
    if(error){setError(error.message);return;}
    setRefresh((value)=>value+1);
  }

  async function openItem(item:Notification){
    if(item.status==="UNREAD")await state(item,"READ");
    go(item.actionPath);
    setOpen(false);
  }

  return (
    <div className="notification-bell">
      <button ref={triggerRef} className={inbox?.counts.unread ? "notification-trigger unread" : "notification-trigger"} onClick={()=>setOpen((value)=>!value)} aria-label="알림">
        <span>●</span>
        <b>알림</b>
        {Boolean(inbox?.counts.unread) && <em>{inbox?.counts.unread}</em>}
      </button>
      {open && typeof document!=="undefined" && createPortal(
        <section
          className="notification-popover notification-popover-portal"
          role="dialog"
          aria-label="센터 알림"
          style={{position:"fixed",top:anchor.top,right:anchor.right,zIndex:200}}
        >
          <header>
            <div><strong>알림</strong><span>ERP 업무·운영 알림</span></div>
            <div className="notification-popover-actions">
              <button onClick={()=>setRefresh((value)=>value+1)}>새로고침</button>
              <button onClick={()=>setOpen(false)} aria-label="알림 닫기">닫기</button>
            </div>
          </header>
          {error && <p className="error">{error}</p>}
          <div className="notification-list">
            {(inbox?.items || []).map((item)=>(
              <article key={item.notificationId} className={(item.status==="UNREAD"?"unread ":"")+(item.severity==="HIGH"?"high":"")}>
                <button className="notification-main" onClick={()=>openItem(item)}>
                  <span>{item.category} · {item.severity==="HIGH"?"우선":"일반"}</span>
                  <strong>{item.title}</strong>
                  {item.body && <p>{item.body}</p>}
                  <small>{fmt(item.lastSeenAt)}{!item.sourceActive?" · 업무 해소됨":""}</small>
                </button>
                <div className="notification-actions">
                  {item.status==="UNREAD" ? <button disabled={busy===item.notificationId} onClick={()=>state(item,"READ")}>읽음</button> : <button disabled={busy===item.notificationId} onClick={()=>state(item,"UNREAD")}>안읽음</button>}
                  <button disabled={busy===item.notificationId} onClick={()=>state(item,"DISMISS")}>숨김</button>
                </div>
              </article>
            ))}
            {!inbox?.items.length && <p className="case-empty">표시할 알림이 없습니다.</p>}
          </div>
        </section>,
        document.body
      )}
    </div>
  );
}
